import jwtDecode from 'jwt-decode';
import React, { createContext, PropsWithChildren, useEffect, useReducer } from 'react';

import { useLocalStorage } from '../../hooks/useLocalStorage';
import { AppAction, AppActions } from './appActions';
import { appReducer } from './appReducer';

export const ID_TOKEN = 'id_token';

export interface AppState {
  loggedIn: boolean;
  /** TODO: combine userId, roles, username into "user" object */
  userId: Nullable<string>;
  roles: Nullable<string[]>;
  username: Nullable<string>;
  visibleComponents: string[];
}

const extractInfoFromToken = (
  decodedToken,
): { roles: Nullable<string[]>; userId: Nullable<string>; username: Nullable<string> } => {
  const originRoles = decodedToken.origin_roles || [];
  const authorities = decodedToken.authorities || [];
  const roles = [...new Set([...originRoles, ...authorities])].sort();

  const userId = decodedToken.principal_id;
  const username = decodedToken.principal_name;

  return { roles, userId, username };
};

const existingToken = localStorage.getItem(ID_TOKEN);
const existingTokenData = existingToken && extractInfoFromToken(jwtDecode<any>(existingToken));

const initialState: AppState = Object.freeze({
  loggedIn: !!existingToken,
  roles: (existingTokenData && existingTokenData.roles) || null, // || null to satisfy strict null checking
  userId: existingTokenData && existingTokenData.userId,
  username: existingTokenData && existingTokenData.username,
  visibleComponents: [],
});

export interface AppContextProps {
  loggedIn: boolean;
  userId: Nullable<string>;
  roles: Nullable<string[]>;
  username: Nullable<string>;
  visibleComponents: string[];
  dispatch: React.Dispatch<AppAction>;
}

export const AppStateContext = createContext<AppContextProps | null>(null);

export const AppStateContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [appState, dispatch] = useReducer(appReducer, initialState);
  const [token] = useLocalStorage<string>(ID_TOKEN);

  useEffect(() => {
    if (token) {
      let decodedToken;
      try {
        decodedToken = jwtDecode<any>(token);
      } catch (e) {
        return dispatch({ type: AppActions.USER_LOGGED_OUT });
      }
      dispatch({ type: AppActions.USER_LOGGED_IN, payload: extractInfoFromToken(decodedToken) });
    } else {
      dispatch({ type: AppActions.USER_LOGGED_OUT });
    }
  }, [token]);

  return (
    <AppStateContext.Provider value={{ ...appState, dispatch }}>
      {children}
    </AppStateContext.Provider>
  );
};
